<script setup lang="ts">
import ManagementOfficeAutoComplete from '@/components/AutoCompletes/ManagementOfficeAutoComplete.vue';
import SingleOperationOfficeAutoComplete from '@/components/AutoCompletes/SingleOperationOfficeAutoComplete.vue';
import DepartmentAutoComplete from '@/components/AutoCompletes/DepartmentAutoComplete.vue';
import getManagementOffice from '@/types/responses/getManagementOffice';
import getOperationOffice from '@/types/responses/getOperationOffice';
import getDepartmentResponse from '@/types/responses/getDepartment';
import {
  OPERATION_OFFICE_TYPE,
    ITTENKO_V2,
    ITTENKO_V3,
    WHITE_SAFETY
} from '@/services/consts';
import { Ref, ref, computed } from 'vue';
import i18n from '@/lang/i18n';

const emits = defineEmits(['search'])
const props = defineProps<{
    loading: boolean
  }>()

const OPERATION_OFFICES_FILTER_PARAMS = {
  tenko_type: [WHITE_SAFETY, ITTENKO_V2, ITTENKO_V3],
}
const hide: Ref<boolean> = ref(false)
const managementOffice: Ref<getManagementOffice | undefined> = ref()
const operationOffice: Ref<getOperationOffice | undefined> = ref()
const department: Ref<getDepartmentResponse | undefined> = ref()
const accountName: Ref<string | null> = ref(null)
const errorMessages: Ref<{[key: string]: string[]}> = ref({
  operationOffice: []
})

const searchAreaClass = computed(() => {
  return hide.value ? "search-area-hide p-4" : "search-area p-4"
})

const title = computed(() => {
  return hide.value ? "" : i18n.global.t('pages.common.filter.title')
})

const searchBtnLabel = computed(() => {
  return props.loading ? i18n.global.t('pages.common.button.loading') : i18n.global.t('pages.common.button.exec');
})

function handleClickSearch() {
  if (!validation()) {
    return;
  }
  resetValidationMessages()
  const params = {
    type_class: OPERATION_OFFICE_TYPE,
    officeable_id: operationOffice.value?.id ?? null,
    account_name: accountName.value,
    department_id: department.value?.id ?? null,
  }
  emits('search', params)
}

function validation() {
  if (!operationOffice.value) {
    errorMessages.value.operationOffice = [i18n.global.t('pages.tenkoAssociation.errorMessage.operationOffice.required')];
    return false;
  }
  return true;
}

function clearForm() {
  managementOffice.value = undefined
  operationOffice.value = undefined
  department.value = undefined
  accountName.value = null
}

function resetValidationMessages() {
  errorMessages.value.operationOffice = []
}

function toggleSidebar() {
  hide.value = !hide.value
}
</script>
<template>
  <div :class="searchAreaClass">
    <h4 class="d-flex justify-content-between mt-0">
      {{ title }}
      <svg
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        fit=""
        preserveAspectRatio="xMidYMid meet"
        focusable="false"
        sandboxuid="0"
        @click="toggleSidebar"
      >
        <template v-if="hide">
          <path d="M7.615 11.795l4.59-4.59-1.41-1.41-6 6 6 6 1.41-1.41-4.59-4.59zm9.59 6v-12h-2v12h2z" fill-rule="evenodd" sandboxuid="0"></path>
        </template>
        <template v-else>
          <path d="M6 7.41L10.438 12 6 16.59 7.363 18l5.802-6-5.802-6L6 7.41zM16.066 6H18v12h-1.934V6z" sandboxuid="0" />
        </template>
      </svg>
    </h4>
    <div :class="`strpied-tabled-with-hover ${hide ? 'd-none' : '' }`">
      <div class="conditions v-theme--light">
        <ManagementOfficeAutoComplete
          v-model="managementOffice"
        />
        <SingleOperationOfficeAutoComplete
          v-model="operationOffice"
          :managementOffice="managementOffice"
          :errorMessages="errorMessages.operationOffice"
          :filterParams="OPERATION_OFFICES_FILTER_PARAMS"
          class="pt-2"
        />
        <DepartmentAutoComplete
          v-model="department"
          :operationOffices="operationOffice ? [operationOffice] : undefined"
          class="pt-2"
        />
        <v-text-field
          class="pt-2"
          :label="i18n.global.t('pages.tenkoAssociation.filter.driverName')"
          v-model="accountName"
          outlined
          hide-details="auto"
        />
        <div class="d-flex flex-column">
          <div class="col-md-3">
            <svg
              version="1.2"
              style="overflow: visible; width: 299px; height: 1px; opacity: 1; mix-blend-mode: normal; rotate: 0deg;"
            >
              <defs>
                <path id="path-1650848226989195" d="M1126 703 C1126 703 1425 703 1425 703"></path>
              </defs>
              <g transform="translate(-1126, -703)">
                <path style="stroke: rgb(194, 186, 186); stroke-width: 1; stroke-linecap: butt; stroke-linejoin: miter; fill: none;" d="M1126 703 C1126 703 1425 703 1425 703"></path>
              </g>
            </svg>
          </div>
          <div class="d-flex search-buttons">
            <v-btn
              class="ma-2 exec-btn"
              @click="handleClickSearch"
              :disabled="props.loading"
              :style="{
                backgroundColor : props.loading ? '#B3B8BB' : ''
              }"
            >
              {{ searchBtnLabel }}
            </v-btn>
            <v-btn
              class="ma-2 reset-btn"
              :disabled="props.loading"
              @click="clearForm"
            >
              {{ i18n.global.t('pages.common.button.reset') }}
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
